export const ROOT_CONTAINER_ID = 'adeff-page-container'
export const DASHBOARD_CONTENT_CONTAINER_ID = 'dashboard-layout-content-container'

export const PAGE_ROOT = '/'
export const DOCS_ROOT = '/doc'
export const PAGE_STATUS = '/status'
export const DASHBOARD_ROOT = '/dashboard'
export const PROFESSOR_ROOT = '/professor'
export const HORIZONTAL_QUICK_PREFIX = '/horizontal-quick'
export const HORIZONTAL_BASE_PREFIX = '/horizontal-base'
export const HORIZONTAL_SURVEY_PREFIX = '/horizontal-survey'
export const PORTRAIT_QUICK_PREFIX = '/portrait-quick'
export const PORTRAIT_BASE_PREFIX = '/portrait-base'
export const PORTRAIT_SURVEY_PREFIX = '/portrait-survey'

export type DashboardLinkKey =
  | 'project'
  | 'projectCreate'
  | 'projectDetail'
  | 'setting'
  | 'account'
  | 'subscription'
  | 'statistic'
  | 'statisticCompare'
  | 'statisticCompareStart'
  | 'statisticCompareInfo'

const DASHBOARD_LINK_KEYS: DashboardLinkKey[] = [
  'project',
  'projectCreate',
  'projectDetail',
  'setting',
  'account',
  'subscription',
  'statistic',
  'statisticCompare',
  'statisticCompareStart',
  'statisticCompareInfo',
]

function createDashboardLinks(prefix: string): Record<DashboardLinkKey, string> {
  return {
    project: `${prefix}${DASHBOARD_ROOT}/project`,
    projectCreate: `${prefix}${DASHBOARD_ROOT}/project/create`,
    projectDetail: `${prefix}${DASHBOARD_ROOT}/project/detail`,
    setting: `${prefix}${DASHBOARD_ROOT}/setting`,
    account: `${prefix}${DASHBOARD_ROOT}/account`,
    subscription: `${prefix}${DASHBOARD_ROOT}/subscription`,
    statistic: `${prefix}${DASHBOARD_ROOT}/statistic/overview`,
    statisticCompare: `${prefix}${DASHBOARD_ROOT}/statistic/compare`,
    statisticCompareStart: `${prefix}${DASHBOARD_ROOT}/statistic/compare/start`,
    statisticCompareInfo: `${prefix}${DASHBOARD_ROOT}/statistic/compare/info`,
  }
}

export const PRODUCT_ROUTE_PREFIX_MAP: Record<ProjectModels.ProjectCategory, string> = {
  'horizontal-quick': HORIZONTAL_QUICK_PREFIX,
  'horizontal-base': HORIZONTAL_BASE_PREFIX,
  'horizontal-survey': HORIZONTAL_SURVEY_PREFIX,
  'portrait-quick': PORTRAIT_QUICK_PREFIX,
  'portrait-base': PORTRAIT_BASE_PREFIX,
  'portrait-survey': PORTRAIT_SURVEY_PREFIX,
}

export const PRODUCT_DASHBOARD_LINKS: Record<
  ProjectModels.ProjectCategory,
  Record<DashboardLinkKey, string>
> = {
  'horizontal-quick': createDashboardLinks(HORIZONTAL_QUICK_PREFIX),
  'horizontal-base': createDashboardLinks(HORIZONTAL_BASE_PREFIX),
  'horizontal-survey': createDashboardLinks(HORIZONTAL_SURVEY_PREFIX),
  'portrait-quick': createDashboardLinks(PORTRAIT_QUICK_PREFIX),
  'portrait-base': createDashboardLinks(PORTRAIT_BASE_PREFIX),
  'portrait-survey': createDashboardLinks(PORTRAIT_SURVEY_PREFIX),
}

const DASHBOARD_LINK_KEY_BY_PATH: Partial<Record<string, DashboardLinkKey>> =
  Object.values(PRODUCT_DASHBOARD_LINKS).reduce((acc, links) => {
    DASHBOARD_LINK_KEYS.forEach((key) => {
      acc[links[key]] = key
    })
    return acc
  }, {} as Partial<Record<string, DashboardLinkKey>>)

export function getProductDashboardLink(
  productCategory: ProjectModels.ProjectCategory,
  key: DashboardLinkKey
) {
  return PRODUCT_DASHBOARD_LINKS[productCategory][key]
}

export function resolveDashboardLinkKeyByPath(pathname: string) {
  return DASHBOARD_LINK_KEY_BY_PATH[pathname]
}

export function resolveProductCategoryByPath(pathname: string): ProjectModels.ProjectCategory {
  if (pathname.startsWith(HORIZONTAL_QUICK_PREFIX)) {
    return 'horizontal-quick'
  }
  if (pathname.startsWith(HORIZONTAL_SURVEY_PREFIX)) {
    return 'horizontal-survey'
  }
  if (pathname.startsWith(PORTRAIT_QUICK_PREFIX)) {
    return 'portrait-quick'
  }
  if (pathname.startsWith(PORTRAIT_BASE_PREFIX)) {
    return 'portrait-base'
  }
  if (pathname.startsWith(PORTRAIT_SURVEY_PREFIX)) {
    return 'portrait-survey'
  }
  return 'horizontal-base'
}

export const PAGE_LINKS = {
  /** 首页相关页面 */
  index: PAGE_ROOT,
  pricing: PAGE_ROOT + 'pricing',
  login: PAGE_ROOT + 'login',
  resetPwd: PAGE_ROOT + 'reset_pwd',
  infoBind: PAGE_ROOT + 'info_bind',
  help: PAGE_ROOT + 'help',
  contact: PAGE_ROOT + 'contact',
  freeTrial: PAGE_ROOT + 'free_trial',
  knowledgeForm: PAGE_ROOT + 'knowledge_form',
  test: PAGE_ROOT + 'test',
  /** 状态相关页面 */
  memberRequest: PAGE_STATUS + 'member_request',

  /** horizontal-base */
  project: PRODUCT_DASHBOARD_LINKS['horizontal-base'].project,
  projectCreate: PRODUCT_DASHBOARD_LINKS['horizontal-base'].projectCreate,
  projectDetail: PRODUCT_DASHBOARD_LINKS['horizontal-base'].projectDetail,
  setting: PRODUCT_DASHBOARD_LINKS['horizontal-base'].setting,
  account: PRODUCT_DASHBOARD_LINKS['horizontal-base'].account,
  subscription: PRODUCT_DASHBOARD_LINKS['horizontal-base'].subscription,
  statistic: PRODUCT_DASHBOARD_LINKS['horizontal-base'].statistic,
  statisticCompare: PRODUCT_DASHBOARD_LINKS['horizontal-base'].statisticCompare,
  statisticCompareStart: PRODUCT_DASHBOARD_LINKS['horizontal-base'].statisticCompareStart,
  statisticCompareInfo: PRODUCT_DASHBOARD_LINKS['horizontal-base'].statisticCompareInfo,

  /** horizontal-quick */
  quickProject: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].project,
  quickProjectCreate: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].projectCreate,
  quickProjectDetail: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].projectDetail,
  quickSetting: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].setting,
  quickAccount: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].account,
  quickSubscription: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].subscription,
  quickStatistic: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].statistic,
  quickStatisticCompare: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].statisticCompare,
  quickStatisticCompareStart: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].statisticCompareStart,
  quickStatisticCompareInfo: PRODUCT_DASHBOARD_LINKS['horizontal-quick'].statisticCompareInfo,

  /** horizontal-survey */
  experienceProject: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].project,
  experienceProjectCreate: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].projectCreate,
  experienceProjectDetail: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].projectDetail,
  experienceSetting: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].setting,
  experienceAccount: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].account,
  experienceSubscription: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].subscription,
  experienceStatistic: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].statistic,
  experienceStatisticCompare: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].statisticCompare,
  experienceStatisticCompareStart: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].statisticCompareStart,
  experienceStatisticCompareInfo: PRODUCT_DASHBOARD_LINKS['horizontal-survey'].statisticCompareInfo,

  /** portrait-base */
  portraitProject: PRODUCT_DASHBOARD_LINKS['portrait-base'].project,
  portraitProjectCreate: PRODUCT_DASHBOARD_LINKS['portrait-base'].projectCreate,
  portraitProjectDetail: PRODUCT_DASHBOARD_LINKS['portrait-base'].projectDetail,
  portraitSetting: PRODUCT_DASHBOARD_LINKS['portrait-base'].setting,
  portraitAccount: PRODUCT_DASHBOARD_LINKS['portrait-base'].account,
  portraitSubscription: PRODUCT_DASHBOARD_LINKS['portrait-base'].subscription,
  portraitStatistic: PRODUCT_DASHBOARD_LINKS['portrait-base'].statistic,
  portraitStatisticCompare: PRODUCT_DASHBOARD_LINKS['portrait-base'].statisticCompare,
  portraitStatisticCompareStart: PRODUCT_DASHBOARD_LINKS['portrait-base'].statisticCompareStart,
  portraitStatisticCompareInfo: PRODUCT_DASHBOARD_LINKS['portrait-base'].statisticCompareInfo,

  /** portrait-quick */
  portraitQuickProject: PRODUCT_DASHBOARD_LINKS['portrait-quick'].project,
  portraitQuickProjectCreate: PRODUCT_DASHBOARD_LINKS['portrait-quick'].projectCreate,
  portraitQuickProjectDetail: PRODUCT_DASHBOARD_LINKS['portrait-quick'].projectDetail,
  portraitQuickSetting: PRODUCT_DASHBOARD_LINKS['portrait-quick'].setting,
  portraitQuickAccount: PRODUCT_DASHBOARD_LINKS['portrait-quick'].account,
  portraitQuickSubscription: PRODUCT_DASHBOARD_LINKS['portrait-quick'].subscription,
  portraitQuickStatistic: PRODUCT_DASHBOARD_LINKS['portrait-quick'].statistic,
  portraitQuickStatisticCompare: PRODUCT_DASHBOARD_LINKS['portrait-quick'].statisticCompare,
  portraitQuickStatisticCompareStart: PRODUCT_DASHBOARD_LINKS['portrait-quick'].statisticCompareStart,
  portraitQuickStatisticCompareInfo: PRODUCT_DASHBOARD_LINKS['portrait-quick'].statisticCompareInfo,

  /** portrait-survey */
  portraitExperienceProject: PRODUCT_DASHBOARD_LINKS['portrait-survey'].project,
  portraitExperienceProjectCreate: PRODUCT_DASHBOARD_LINKS['portrait-survey'].projectCreate,
  portraitExperienceProjectDetail: PRODUCT_DASHBOARD_LINKS['portrait-survey'].projectDetail,
  portraitExperienceSetting: PRODUCT_DASHBOARD_LINKS['portrait-survey'].setting,
  portraitExperienceAccount: PRODUCT_DASHBOARD_LINKS['portrait-survey'].account,
  portraitExperienceSubscription: PRODUCT_DASHBOARD_LINKS['portrait-survey'].subscription,
  portraitExperienceStatistic: PRODUCT_DASHBOARD_LINKS['portrait-survey'].statistic,
  portraitExperienceStatisticCompare: PRODUCT_DASHBOARD_LINKS['portrait-survey'].statisticCompare,
  portraitExperienceStatisticCompareStart: PRODUCT_DASHBOARD_LINKS['portrait-survey'].statisticCompareStart,
  portraitExperienceStatisticCompareInfo: PRODUCT_DASHBOARD_LINKS['portrait-survey'].statisticCompareInfo,

  /** doc 相关页面 */
  privacy: DOCS_ROOT + '/privacy_policy',
  termsOfService: DOCS_ROOT + '/terms_of_service',

  /** 专家评测页面 */
  professorLogin: PROFESSOR_ROOT + '/login',
  professorProject: PROFESSOR_ROOT + '/project',
  professorProjectDetail: PROFESSOR_ROOT + '/project/detail',

}
